import translations from '../../../../../../../../translations';
import type { ComposerElement } from '../../../../../../../../types/pageEditor';
import FormGroup from '../../../../../../../generic/FormGroup';
import { COMPONENT_CONFIGURATION_LABELS } from '../../../../../../constants';
import ConfigSection from '../../ConfigSection';

interface Props {
    componentConfigOptions: { [key: string]: boolean } | null;
    modifierValues: Partial<ComposerElement> | null;
    updateEditable: (isEditable: boolean) => void;
    updateMultiSelect: (isMultiSelect: boolean) => void;
    updateSearchable: (isSearchable: boolean) => void;
    updateSearchableDisplayColumns: (hasSearchableDisplayColumns: boolean) => void;
    updateSearchTermPersisted: (isSearchTermPersisted: boolean) => void;
    updateAutoUpload: (autoUpload: boolean) => void;
    updateSortableColumns: (sortable: boolean) => void;
    updateCsvExportButton: (csvButton: boolean) => void;
    updateCsvFileName: (fileName: string) => void;
    updateAutoComplete: (autoCompleteValue: string) => void;
}

const ModifiersSection = ({
    componentConfigOptions,
    modifierValues,
    updateEditable,
    updateMultiSelect,
    updateSearchable,
    updateSearchableDisplayColumns,
    updateSearchTermPersisted,
    updateAutoUpload,
    updateSortableColumns,
    updateCsvExportButton,
    updateCsvFileName,
    updateAutoComplete,
}: Props) => {
    if (!(componentConfigOptions || modifierValues)) {
        return null;
    }

    const {
        hasEditable,
        hasMultiselect,
        hasSearchable,
        hasSearchTermPersisted,
        hasAutoUpload,
        hasSortableColumns,
        hasCsvExportButton,
        hasCsvFileName,
        hasAutocomplete,
    } = componentConfigOptions ?? {};

    const {
        isEditable,
        isMultiSelect,
        isSearchable,
        isSearchableDisplayColumns,
        isSearchTermPersisted,
        attributes,
    } = modifierValues ?? {};

    const isAutoUpload =
        typeof attributes?.['$autoUpload'] === 'string' &&
        attributes?.['$autoUpload'].toLowerCase() === 'true';

    const isSortableColumns =
        typeof attributes?.['$hasSortableColumns'] === 'string' &&
        attributes?.['$hasSortableColumns'].toLowerCase() === 'true';

    const isCsvExportButton =
        typeof attributes?.['$exportCsvButton'] === 'string' &&
        attributes?.['$exportCsvButton'].toLowerCase() === 'true';

    const csvFileName = attributes?.['$csvFileName'] as string;

    const autocomplete = (attributes?.['$autocomplete'] ?? '') as string;

    return (
        <>
            <ConfigSection contentLayout="flex-row" dataTestId="modifiers-section">
                {hasEditable && (
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                onChange={() => {
                                    updateEditable(!isEditable);
                                }}
                                checked={isEditable}
                            />
                            {COMPONENT_CONFIGURATION_LABELS['EDITABLE']}
                        </label>
                    </div>
                )}

                {hasMultiselect && (
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                onChange={() => {
                                    updateMultiSelect(!isMultiSelect);
                                }}
                                checked={isMultiSelect}
                            />
                            {COMPONENT_CONFIGURATION_LABELS['MULTISELECT']}
                        </label>
                    </div>
                )}

                {hasSearchable && (
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                onChange={() => {
                                    updateSearchable(!isSearchable);
                                }}
                                checked={isSearchable}
                            />
                            {COMPONENT_CONFIGURATION_LABELS['SEARCHABLE']}
                        </label>
                    </div>
                )}

                {hasSearchable && isSearchable && (
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                onChange={() => {
                                    updateSearchableDisplayColumns(!isSearchableDisplayColumns);
                                }}
                                checked={isSearchableDisplayColumns}
                            />
                            {COMPONENT_CONFIGURATION_LABELS['SEARCHABLEDISPLAYCOLUMNS']}
                        </label>
                    </div>
                )}

                {hasSearchable && isSearchable && hasSearchTermPersisted && (
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                onChange={() => {
                                    updateSearchTermPersisted(!isSearchTermPersisted);
                                }}
                                checked={isSearchTermPersisted}
                            />
                            {COMPONENT_CONFIGURATION_LABELS['SEARCHTERMPERSISTED']}
                        </label>
                    </div>
                )}

                {hasAutoUpload && (
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                onChange={() => {
                                    updateAutoUpload(!isAutoUpload);
                                }}
                                checked={isAutoUpload}
                            />
                            {COMPONENT_CONFIGURATION_LABELS['AUTO_UPLOAD']}
                        </label>
                    </div>
                )}

                {hasSortableColumns && (
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                onChange={() => {
                                    updateSortableColumns(!isSortableColumns);
                                }}
                                checked={isSortableColumns}
                            />
                            {COMPONENT_CONFIGURATION_LABELS['SORTABLE_COLUMNS']}
                        </label>
                    </div>
                )}

                {hasCsvExportButton && (
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                onChange={() => {
                                    updateCsvExportButton(!isCsvExportButton);
                                }}
                                checked={isCsvExportButton}
                            />
                            {COMPONENT_CONFIGURATION_LABELS['EXPORT_CSV_BUTTON']}
                        </label>
                    </div>
                )}
            </ConfigSection>

            {hasCsvExportButton && hasCsvFileName && isCsvExportButton && (
                <ConfigSection>
                    <FormGroup
                        label={COMPONENT_CONFIGURATION_LABELS['CSV_FILE_NAME']}
                        htmlFor="csv-file-name"
                    >
                        <div className="grouped-control">
                            <input
                                onChange={(e) => updateCsvFileName(e.target.value)}
                                className="with-extension form-control"
                                id="csv-file-name"
                                type="text"
                                value={csvFileName}
                            />
                            <div className="grouped-control-extension align-center flex padding">
                                .csv
                            </div>
                        </div>
                    </FormGroup>
                </ConfigSection>
            )}

            {hasAutocomplete && (
                <ConfigSection>
                    <FormGroup
                        label={COMPONENT_CONFIGURATION_LABELS['AUTOCOMPLETE']}
                        htmlFor="autocomplete"
                    >
                        <input
                            onChange={(e) => updateAutoComplete(e.target.value)}
                            className="form-control"
                            id="autocomplete"
                            type="text"
                            value={autocomplete}
                        />
                    </FormGroup>
                    <span className="help-block">
                        {translations.PAGE_BUILDER_autocomplete_help_text}
                    </span>
                </ConfigSection>
            )}
        </>
    );
};

export default ModifiersSection;
